import * as React from 'react';
import { connect } from 'react-redux'
import { Dispatch, bindActionCreators, AnyAction } from 'redux';

import PromoCodeTextBox from './promo-code-textbox';
import PromoCodeInfo from './promo-code-info';
import PromoCodeConflict from './promo-code-conflict';

import { formatString } from '../../helpers/string-format';
import { IAltText } from '../../classes/alt-text';
import { IAppState } from '../../reducers';
import { IPromoCodeDetails } from '../../classes/promo-code-details';
import { IPromoCodeModel } from '../../filter/reducers/promo-reducer';

// TODO move this
import { applyPromoCode, cancelPromoApply, emptyCart, removePromoCode, errorPromoApply } from '../../filter/actions/promo-actions';

interface IStateProps {
    promoCodeModel: IPromoCodeModel
    currentPromoCode: IPromoCodeDetails

    altText: IAltText;
    promoCodeHeading: string;
    promoCodeRedirect: string;
    enablePromoCodes: boolean;
    showPromoCodeSectionOnPageLoad: boolean;
    promoCodeSuccessMessage: string;
    modalCancelButtonText: string;
    emptyCartButtonText: string;
}

interface IDispatchProps {
    onApplyPromoClick: (promoCode: string) => void;
    onCancelPromoClick: () => void;
    onEmptyCartClick: (promoCode: string) => void;
    onRemovePromoClick: () => void;
    clearPromoCodeMessage: () => void;
}

interface IOwnProps {
    culture: string;
    collapseDown: boolean; // control if the promo code section should collapse down or up
    showMOSMessage?: boolean;
    MOSMessage?: string;
}

type IProps = IStateProps & IDispatchProps & IOwnProps

interface IState {
    checked: boolean;
}

/** PromoCode */
export class PromoCode extends React.PureComponent<IProps, IState>{

    constructor(props: IProps) {
        super(props);
        this.state = {
            checked: props.currentPromoCode != null || props.showPromoCodeSectionOnPageLoad
        };
    }

    /** Set checked only if we actually recieve a promo code with an Id */
    componentWillReceiveProps(nextProps: IProps) {
        if (nextProps.promoCodeModel && nextProps.promoCodeModel.promoCodeId > 0) {
            this.setState({ checked: true });
        }
    }

    render() {
        if (!this.props.enablePromoCodes) {
            return null;
        }

        return this.props.showMOSMessage ? this.renderMOSMessage() : this.renderPromoInterface()
    }

    renderPromoInterface = () => {
        const { checked } = this.state;
        return (
            <section className="filters-section promo-code-section">
                <div className="filters-section-heading">
                    {this.renderButtons()}
                </div>
                {checked && this.renderDetail()}
            </section>
        )
    }

    /** Render an accordion toggle if there is no promo code, or a remove button if there is */
    renderButtons = () => {
        if (this.props.promoCodeModel.promoCodeId > 0) {
            return (
                <button className="promo-code-info-btn" name="rmvPromoInput" onClick={this.props.onRemovePromoClick}>Remove Promo</button>
            )
        }

        const expanded = this.state.checked ? 'true' : 'false'
        const toggled = (this.props.collapseDown ? this.state.checked : !this.state.checked) ? ' toggled' : '';

        const useTag = `<use xlink:href="#icon-arrow-down" />`;

        return (
            <button className="btn-toggle-full" onClick={this.onToggleChecked} aria-expanded={expanded}>
                <h2 className="heading-general">{this.props.promoCodeHeading}</h2>
                <svg className={"filters-section-heading-toggles-icon" + toggled} aria-hidden="true"
                    dangerouslySetInnerHTML={{ __html: useTag }} />
            </button>
        )
    }

    /** Render information and errors related to the promo code */
    renderDetail = () => {
        return (
            <div className="fade-in">
                {this.renderInfo()}
                {this.renderError()}
            </div>
        )
    }

    /** Render the info about the promo code, or a text box to enter one */
    renderInfo = () => {
        const { promoCodeModel, currentPromoCode } = this.props

        if (currentPromoCode != null && promoCodeModel && promoCodeModel.promoCodeId > 0) {
            return (
                <PromoCodeInfo
                    promoCodeRedirect={this.props.promoCodeRedirect}
                    promoCodeDescription={currentPromoCode && currentPromoCode.description || ''}
                    promoCodeText={this.props.promoCodeSuccessMessage}
                    promoCodeTextTitle={currentPromoCode && currentPromoCode.promo || ''}
                    promoCodeText4={currentPromoCode && currentPromoCode.text4 || ''}
                    promoCodeText5={currentPromoCode && currentPromoCode.text5 || ''}
                    promoCodeText6={currentPromoCode && currentPromoCode.text6 || ''} />
            )
        }

        return (
            <PromoCodeTextBox
                promoCodeTitle={currentPromoCode && currentPromoCode.promo || ''}
                applying={promoCodeModel && promoCodeModel.applying}
                promoErrorMessage={promoCodeModel && promoCodeModel.promoErrorMessage || ''}
                onApplyPromoClick={this.props.onApplyPromoClick}
                onCancelPromoClick={this.props.onCancelPromoClick}
                clearPromoErrorMessage={this.props.clearPromoCodeMessage}
            />
        )
    }

    /** Render the errors if any, either a conflict or general error */
    renderError = () => {
        if (this.props.promoCodeModel.promoConflict) {
            return (
                <PromoCodeConflict
                    promoCode={this.props.promoCodeModel.promoCode}
                    emptyCartButtonText={this.props.emptyCartButtonText}
                    modalCancelButtonText={this.props.modalCancelButtonText}
                    promoErrorMessage={this.props.promoCodeModel.promoErrorMessage}
                    onCancelPromoClick={this.props.onCancelPromoClick}
                    onEmptyCartClick={this.props.onEmptyCartClick} />
            )
        }

        if (this.props.promoCodeModel.promoErrorMessage) {
            return (
                <p className="promo-code-apply-error" id="promo-code-error">
                    {this.props.promoCodeModel.promoErrorMessage}
                </p>
            );
        }
    }

    /** Show message if there's already cart contents, that they can't add a promo right now */
    renderMOSMessage = () => {
        return (
            <div className="filters-section">
                <div className="filters-section-heading">
                    <p>{this.props.MOSMessage}</p>
                </div>
            </div>
        );
    }

    /** Invert the value of checked */
    onToggleChecked = () => {
        this.setState({ checked: !this.state.checked })
    }
}

/**
 * 
 * @param state
 */
const mapStateToProps = (state: IAppState): IStateProps => ({
    promoCodeModel: state.promoCodeModel,
    currentPromoCode: state.appSettingsModel.currentPromoCode,
    altText: state.appSettingsModel.altText,
    emptyCartButtonText: state.appSettingsModel.emptyCartButtonText,
    enablePromoCodes: state.appSettingsModel.enablePromoCodes,
    modalCancelButtonText: state.appSettingsModel.modalCancelButtonText,
    promoCodeSuccessMessage: state.appSettingsModel.promoCodeSuccessMessage,
    promoCodeHeading: state.appSettingsModel.promoCodeTitle,
    showPromoCodeSectionOnPageLoad: state.appSettingsModel.showPromoCodeSectionOnPageLoad
} as IStateProps)

/**
 * 
 * @param dispatch
 */
const mapDispatchToProps = (dispatch: Dispatch<AnyAction>, ownProps: IOwnProps): IDispatchProps => bindActionCreators({
    onApplyPromoClick: (promoCode: string) => applyPromoCode(promoCode, ownProps.culture, false),
    onCancelPromoClick: cancelPromoApply,
    onEmptyCartClick: (promoCode: string) => emptyCart(promoCode, ownProps.culture),
    onRemovePromoClick: () => removePromoCode(ownProps.culture, false),
    clearPromoCodeMessage: () => dispatch(errorPromoApply(""))
}, dispatch)


export default connect<IStateProps, IDispatchProps, IOwnProps>(mapStateToProps, mapDispatchToProps)(PromoCode)